import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { media } from '@config/breakpoints';
import { spacing } from '@config/spacing';

/*
 * A swipe track for the narrow screens where a section's cards stand in
 * a row instead of a stack: which card is current (following the swipe),
 * a way to go to one, and — while the page plays that section — a step
 * to the next. Wider layouts show every card at once; the track doesn't
 * scroll there, so the steps do nothing.
 */

/** Scroll a track so card `i` sits at its start. */
function scrollToCard(track: HTMLElement | null, i: number): void {
  const card = track?.children[i] as HTMLElement | undefined;
  if (!track || !card) return;
  track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
}

/**
 * `loop`: a set of examples wraps back to the first; a numbered sequence
 * stops on its last step rather than showing step one again as the page
 * moves on.
 */
export function useCarousel<T extends HTMLElement>(
  count: number,
  playing: boolean,
  stepMs: number,
  { loop = true }: { loop?: boolean } = {},
) {
  const trackRef = useRef<T>(null);
  const [current, setCurrent] = useState(0);
  const currentRef = useRef(0);
  currentRef.current = current;

  // The dots follow the swipe.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const onScroll = () => {
      const card = track.firstElementChild as HTMLElement | null;
      if (!card) return;
      const pitch = card.offsetWidth + parseFloat(getComputedStyle(track).columnGap || '0');
      setCurrent(Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / pitch))));
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [count]);

  // While the page plays this section, step through the cards.
  useEffect(() => {
    if (!playing) return undefined;
    // A sequence left on its last step starts over when the page plays it again.
    if (!loop && currentRef.current >= count - 1) scrollToCard(trackRef.current, 0);
    const id = window.setInterval(() => {
      const track = trackRef.current;
      if (!track || track.scrollWidth <= track.clientWidth + 1) return;
      const next = currentRef.current + 1;
      if (next >= count && !loop) return;
      scrollToCard(track, next % count);
    }, stepMs);
    return () => window.clearInterval(id);
  }, [playing, count, stepMs, loop]);

  return { trackRef, current, goTo: (i: number) => scrollToCard(trackRef.current, i) };
}

type DotsProps = {
  labels: readonly string[];
  current: number;
  onSelect: (i: number) => void;
  /** Read out for the group, e.g. "Demos". */
  label: string;
  /** The width from which every card shows at once, and the dots go. */
  hideFrom: 'sm' | 'md' | 'lg';
};

export const CarouselDots: React.FC<DotsProps> = ({ labels, current, onSelect, label, hideFrom }) => (
  <Dots role="tablist" aria-label={label} $hideFrom={hideFrom}>
    {labels.map((name, i) => (
      <Dot
        key={name}
        type="button"
        role="tab"
        aria-selected={i === current}
        aria-label={name}
        $current={i === current}
        onClick={() => onSelect(i)}
      />
    ))}
  </Dots>
);

const Dots = styled.div<{ $hideFrom: 'sm' | 'md' | 'lg' }>`
  display: flex;
  justify-content: center;
  gap: ${spacing.sm};
  margin-top: ${spacing.sm};

  ${({ $hideFrom }) => media[$hideFrom]} {
    display: none;
  }
`;

const Dot = styled.button<{ $current: boolean }>`
  width: ${({ $current }) => ($current ? '24px' : '8px')};
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: ${({ $current }) => ($current ? colors.accent.coral.extraDark : colors.highlight.extraLight)};
  opacity: ${({ $current }) => ($current ? 1 : 0.6)};
  cursor: pointer;
  transition:
    width 0.3s ease,
    background-color 0.3s ease;
`;
